import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/contexts/NotifyContext";
import { queryAlumniRecordStats } from "@/services/DirectoryService";
import axios from "axios";
import { AlumniRecordStats } from "iasma-datatypes";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Badge } from "../ui/badge";
import { ArrowUpDown, GraduationCap, Mail, UserPlus, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

type SortKey = keyof AlumniRecordStats;

const columns: { key: SortKey; label: string }[] = [
	{ key: "Graduation", label: "Angkatan" },
	{ key: "Records", label: "Total records" },
	{ key: "SelfEntryRecords", label: "Self-Entry records" },
	{ key: "GmailRegistereds", label: "Registrasi Gmail" },
];

function sum(data: AlumniRecordStats[], key: SortKey) {
	return data.reduce((acc, row) => acc + row[key], 0);
}
export function DirectoryRoot() {
	const { session, setToken } = useAuth()
	const { setNotify } = useNotify();
	const navigate = useNavigate();
	const [recStats, setRecStats] = useState<AlumniRecordStats[]>([])
	const [sortKey, setSortKey] = useState<SortKey>("Graduation");
	const [sortAsc, setSortAsc] = useState(true);

	const sorted = useMemo(() => {
		const rows = [...recStats];
		rows.sort((a, b) => (sortAsc ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]));
		return rows;
	}, [recStats, sortKey, sortAsc]);

	const totals = useMemo(
		() => ({
			Records: sum(recStats, "Records"),
			SelfEntryRecords: sum(recStats, "SelfEntryRecords"),
			GmailRegistereds: sum(recStats, "GmailRegistereds"),
		}),
		[recStats]
	);

	const handleSort = (key: SortKey) => {
		if (key === sortKey) {
			setSortAsc((prev) => !prev);
		} else {
			setSortKey(key);
			setSortAsc(true);
		}
	};

	const summaryCards = [
		{
			label: "Total records",
			value: totals.Records,
			icon: Users,
			accent: "text-blue-600 bg-blue-50",
		},
		{
			label: "Self-Entry records",
			value: totals.SelfEntryRecords,
			icon: UserPlus,
			accent: "text-amber-600 bg-amber-50",
		},
		{
			label: "Registrasi Gmail",
			value: totals.GmailRegistereds,
			icon: Mail,
			accent: "text-emerald-600 bg-emerald-50",
		},
		{
			label: "Jumlah Angkatan",
			value: recStats.length,
			icon: GraduationCap,
			accent: "text-violet-600 bg-violet-50",
		},
	];

	useEffect(() => {
		async function fetchAlumniRecordStat() {
			try {
				setNotify(null)
				setToken(axios)
				const result: AlumniRecordStats[] = await queryAlumniRecordStats()
				setRecStats(result)
			} catch (error) {
				setNotify({ title: "Error Fetching Alumni Record Stat", error, severity: 'error' });
			}
		}

		fetchAlumniRecordStat()
	}, []);

	const onClickRow = (graduation: number) => {
		navigate(`/directory/iasma/${graduation}`);
	}

	return (<div className="w-full max-w-5xl mx-auto p-6 space-y-6">
		<div>
			<h1 className="text-2xl font-semibold tracking-tight text-slate-900">
				Statistik Basis Data Alumni
			</h1>
			<p className="text-sm text-slate-500 mt-1">
				Dengan rincian record per-angkatan.
			</p>
		</div>

		{/* Summary cards */}
		<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
			{summaryCards.map(({ label, value, icon: Icon, accent }) => (
				<Card key={label} className="shadow-sm">
					<CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
						<CardTitle className="text-xs font-medium text-slate-500">
							{label}
						</CardTitle>
						<div className={`rounded-full p-1.5 ${accent}`}>
							<Icon className="h-4 w-4" />
						</div>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-semibold text-slate-900">
							{value.toLocaleString()}
						</div>
					</CardContent>
				</Card>
			))}
		</div>

		{/* Data table */}
		<Card className="shadow-sm">
			<CardContent className="p-0">
				<Table containerClassName="max-h-[calc(100vh-490px)] overflow-y-auto">
					<TableHeader>
						<TableRow className="bg-slate-50 hover:bg-slate-50">
							{columns.map(({ key, label }) => (
								<TableHead key={key} className="sticky top-0 z-10 bg-slate-50 whitespace-nowrap">
									<button
										type="button"
										onClick={() => handleSort(key)}
										className="flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900 transition-colors"
									>
										{label}
										<ArrowUpDown
											className={`h-3.5 w-3.5 ${sortKey === key ? "text-slate-900" : "text-slate-300"
												}`}
										/>
									</button>
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((row) => {
							const rate =
								row.Records > 0
									? Math.round((row.SelfEntryRecords / row.Records) * 100)
									: 0;
							return (
								<TableRow key={row.Graduation} className="hover:bg-slate-50/60" onClick={() => onClickRow(row.Graduation)}>
									<TableCell className="font-medium text-slate-900">
										{row.Graduation}
									</TableCell>
									<TableCell className="text-slate-700">{row.Records}</TableCell>
									<TableCell className="text-slate-700">
										<div className="flex items-center gap-2">
											<span>{row.SelfEntryRecords}</span>
											<Badge
												variant="secondary"
												className="text-xs font-normal text-slate-500"
											>
												{rate}%
											</Badge>
										</div>
									</TableCell>
									<TableCell className="text-slate-700">
										{row.GmailRegistereds}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	</div>)
	
}